import { Injectable, OnDestroy, inject } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { first } from 'rxjs/operators';
import { NavigationService } from '../core/navigation.service';
import { LocationService } from '../platform/location.service';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { Position } from 'src/app/models/position';
import { Route, RoutePoint } from 'src/app/models/route';

@Injectable({
  providedIn: 'root'
})
export class NavigationReplayService implements OnDestroy {

  public replayMode: boolean = false;

  private replayTimerPeriods = [1000, 750, 500, 250, 150, 50];
  private replayTimerSpeeds = [0, 3, 10, 20, 30, 40];
  public replayTimerPeriodIndex: number = 1;

  private fakeGpsIterationIndex: number = 0;
  private pointsWithTime: { mPosition: number[], mDateTime: Date | null, mHeight: number | null, mBearing: number, mSpeed: number | null }[] = [];
  private replayTimer: Subscription | null = null;
  private keydownHandler: ((evt: KeyboardEvent) => void) | null = null;

  private readonly navigationService = inject(NavigationService);
  private readonly locationService = inject(LocationService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);

  constructor() {
    this.initKeyboardControls();
  }

  ngOnDestroy(): void {
    this.stop();
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }
  }

  private initKeyboardControls(): void {
    this.keydownHandler = (evt: KeyboardEvent) => {
      if (!this.replayMode) return;

      if (evt.key === 's' && this.replayTimerPeriodIndex > -1) {
        this.replayTimerPeriodIndex--;
        this.consoleLoggerService.log('REPLAY LEVEL: ' + this.replayTimerPeriodIndex);
      }
      if (evt.key === 'w' && this.replayTimerPeriodIndex < this.replayTimerPeriods.length - 1) {
        this.replayTimerPeriodIndex++;
        this.consoleLoggerService.log('REPLAY LEVEL: ' + this.replayTimerPeriodIndex);
        if (this.replayTimerPeriodIndex === 0 && this.fakeGpsIterationIndex < this.pointsWithTime.length) {
          this.fakeGpsIteration();
        }
      }
    };
    document.addEventListener('keydown', this.keydownHandler);
  }

  public startReplay(routeData: Route): void {
    this.stop();
    this.fakeGpsIterationIndex = 0;
    this.pointsWithTime = this.navigationService.generateFakeGpsData(routeData.RoutePoints);
    this.locationService.setFakeGPSReplayMode(true);
    this.fakeGpsIteration();
  }

  public stop(): void {
    if (this.replayTimer) {
      this.replayTimer.unsubscribe();
      this.replayTimer = null;
    }
    this.locationService.setFakeGPSReplayMode(false);
  }

  private fakeGpsIteration(): void {
    const i = this.fakeGpsIterationIndex;
    if (!this.pointsWithTime || i >= this.pointsWithTime.length) {
      if (this.replayTimer) {
        this.replayTimer.unsubscribe();
        this.replayTimer = null;
      }
      return;
    }

    const position = new Position(
      this.pointsWithTime[i].mPosition[0],
      this.pointsWithTime[i].mPosition[1],
      this.replayTimerSpeeds[this.replayTimerPeriodIndex] ?? 0,
      1, 1, 1,
      this.pointsWithTime[i].mBearing,
      new Date().getTime()
    );

    this.locationService.setLastPosition(position);

    if (i === this.pointsWithTime.length - 1) {
      // Reached the end of the track: stop here and leave the index at the last
      // point so a later resume doesn't rewind the cursor back to the start.
      if (this.replayTimer) {
        this.replayTimer.unsubscribe();
        this.replayTimer = null;
      }
      return;
    }

    this.fakeGpsIterationIndex++;

    if (this.replayTimerPeriods.length > this.replayTimerPeriodIndex && this.replayTimerPeriodIndex !== -1) {
      this.replayTimer = timer(this.replayTimerPeriods[this.replayTimerPeriodIndex])
        .pipe(first())
        .subscribe(() => {
          this.fakeGpsIteration();
        });
    }
  }
}
